import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import cccApi, { EnvioPropostaData, RecuperacaoMarcacao } from '../api/cccApi';
import { getRepresentanteEmailFromMarcacao } from '../utils/recuperacaoMarcacaoDisplay';
import { ENVIO_PROPOSTA_NAO_MOTIVOS } from '../config/envioProposta';

type Props = {
  marcacao: RecuperacaoMarcacao;
  marcacaoId: string;
  initial?: EnvioPropostaData | null;
  disabled?: boolean;
  onSaved?: (marcacao: RecuperacaoMarcacao) => void;
};

const formatSavedAt = (iso?: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso.replace('Z', '')).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const RecuperacaoEnvioPropostaForm: React.FC<Props> = ({
  marcacao,
  marcacaoId,
  initial,
  disabled,
  onSaved,
}) => {
  const defaultRepresentanteEmail = useMemo(
    () => getRepresentanteEmailFromMarcacao(marcacao),
    [marcacao]
  );
  const motivoLabelMap = useMemo(
    () => new Map(ENVIO_PROPOSTA_NAO_MOTIVOS.map((m) => [m.key, m.label])),
    []
  );

  const [propostaEnviada, setPropostaEnviada] = useState<boolean | null>(
    typeof initial?.proposta_enviada === 'boolean' ? initial.proposta_enviada : null
  );
  const [dataEnvio, setDataEnvio] = useState(initial?.data_envio || '');
  const [enviadoPara, setEnviadoPara] = useState(
    initial?.enviado_para?.trim() || defaultRepresentanteEmail || ''
  );
  const [motivoNaoEnviada, setMotivoNaoEnviada] = useState(initial?.motivo_nao_enviada || '');
  const [motivoNaoEnviadaOutro, setMotivoNaoEnviadaOutro] = useState(
    initial?.motivo_nao_enviada_outro || ''
  );
  const [fornecedorAtual, setFornecedorAtual] = useState(initial?.fornecedor_atual || '');
  const [dataFimContrato, setDataFimContrato] = useState(initial?.data_fim_contrato || '');
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<{
    updated_at?: string | null;
    updated_by_name?: string | null;
  }>({
    updated_at: initial?.updated_at,
    updated_by_name: initial?.updated_by_name,
  });

  useEffect(() => {
    setPropostaEnviada(typeof initial?.proposta_enviada === 'boolean' ? initial.proposta_enviada : null);
    setDataEnvio(initial?.data_envio || '');
    const savedEmail = initial?.enviado_para?.trim();
    setEnviadoPara(savedEmail || defaultRepresentanteEmail || '');
    setMotivoNaoEnviada(initial?.motivo_nao_enviada || '');
    setMotivoNaoEnviadaOutro(initial?.motivo_nao_enviada_outro || '');
    setFornecedorAtual(initial?.fornecedor_atual || '');
    setDataFimContrato(initial?.data_fim_contrato || '');
    setMeta({
      updated_at: initial?.updated_at,
      updated_by_name: initial?.updated_by_name,
    });
  }, [
    marcacaoId,
    initial?.proposta_enviada,
    initial?.data_envio,
    initial?.enviado_para,
    initial?.motivo_nao_enviada,
    initial?.motivo_nao_enviada_outro,
    initial?.fornecedor_atual,
    initial?.data_fim_contrato,
    initial?.updated_at,
    initial?.updated_by_name,
    defaultRepresentanteEmail,
  ]);

  const handleSave = async () => {
    if (disabled || saving) return;
    setSaving(true);
    try {
      const res = await cccApi.updateEnvioProposta(marcacaoId, {
        proposta_enviada: propostaEnviada,
        data_envio: dataEnvio.trim() || null,
        enviado_para: enviadoPara.trim() || null,
        motivo_nao_enviada: motivoNaoEnviada || null,
        motivo_nao_enviada_outro: motivoNaoEnviadaOutro.trim() || null,
        fornecedor_atual: fornecedorAtual.trim() || null,
        data_fim_contrato: dataFimContrato.trim() || null,
      });
      if (!res.success || !res.marcacao) {
        message.error(res.message || 'Não foi possível guardar o envio da proposta');
        return;
      }
      const saved = res.marcacao.recuperacao_claim?.envio_proposta;
      if (saved) {
        setMeta({
          updated_at: saved.updated_at,
          updated_by_name: saved.updated_by_name,
        });
      }
      onSaved?.(res.marcacao);
      message.success('Dados de envio guardados');
    } finally {
      setSaving(false);
    }
  };

  const savedLabel = formatSavedAt(meta.updated_at);

  return (
    <div className="rec-envio-proposta">
      <div className="rec-envio-proposta-check">
        <span>
          Proposta Enviada? <span className="rec-required-asterisk">*</span>
        </span>
        <div style={{ display: 'flex', gap: 16 }}>
          <label>
            <input
              type="radio"
              name={`proposta-enviada-${marcacaoId}`}
              checked={propostaEnviada === true}
              disabled={disabled}
              onChange={() => setPropostaEnviada(true)}
            />{' '}
            Sim
          </label>
          <label>
            <input
              type="radio"
              name={`proposta-enviada-${marcacaoId}`}
              checked={propostaEnviada === false}
              disabled={disabled}
              onChange={() => setPropostaEnviada(false)}
            />{' '}
            Não
          </label>
        </div>
      </div>

      {propostaEnviada === true && (
        <div className="rec-envio-proposta-fields">
          <label className="rec-envio-proposta-field">
            <span className="rec-envio-proposta-label">
              Data de Envio <span className="rec-required-asterisk">*</span>
            </span>
            <input
              type="date"
              className="rec-envio-proposta-input"
              value={dataEnvio}
              disabled={disabled}
              onChange={(e) => setDataEnvio(e.target.value)}
            />
          </label>

          <label className="rec-envio-proposta-field">
            <span className="rec-envio-proposta-label">
              Enviado para <span className="rec-required-asterisk">*</span>
            </span>
            <input
              type="email"
              className="rec-envio-proposta-input"
              value={enviadoPara}
              disabled={disabled}
              placeholder={defaultRepresentanteEmail || 'email@exemplo.pt'}
              onChange={(e) => setEnviadoPara(e.target.value)}
            />
          </label>
        </div>
      )}

      {propostaEnviada === false && (
        <div className="rec-motivo">
          <fieldset className="rec-motivo-category" disabled={disabled}>
            <legend className="rec-motivo-category-title">
              Motivo <span className="rec-required-asterisk">*</span>
            </legend>
            <div className="rec-motivo-options">
              {ENVIO_PROPOSTA_NAO_MOTIVOS.map((opt) => {
                const isSelected = motivoNaoEnviada === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`rec-motivo-option ${isSelected ? 'is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => setMotivoNaoEnviada(isSelected ? '' : opt.key)}
                  >
                    <span className="rec-motivo-option-check" aria-hidden>
                      {isSelected ? '✓' : ''}
                    </span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>
          <div className="rec-motivo-feedback-block">
            <label className="rec-motivo-feedback-label" htmlFor={`envio-motivo-outro-${marcacaoId}`}>
              Comentário adicional
              {motivoNaoEnviada === 'outro' && <span className="rec-required-asterisk"> *</span>}
            </label>
            <textarea
              id={`envio-motivo-outro-${marcacaoId}`}
              className="rec-motivo-feedback-input"
              rows={4}
              value={motivoNaoEnviadaOutro}
              disabled={disabled}
              placeholder="Detalhe o motivo, contexto ou próximos passos…"
              onChange={(e) => setMotivoNaoEnviadaOutro(e.target.value)}
            />
          </div>
          {motivoNaoEnviada === 'fidelizado' && (
            <div className="rec-envio-proposta-fields">
              <label className="rec-envio-proposta-field">
                <span className="rec-envio-proposta-label">
                  Fornecedor Atual <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  type="text"
                  className="rec-envio-proposta-input"
                  value={fornecedorAtual}
                  disabled={disabled}
                  placeholder="Nome do fornecedor atual"
                  onChange={(e) => setFornecedorAtual(e.target.value)}
                />
              </label>
              <label className="rec-envio-proposta-field">
                <span className="rec-envio-proposta-label">
                  Data de Fim do Contrato <span className="rec-required-asterisk">*</span>
                </span>
                <input
                  type="date"
                  className="rec-envio-proposta-input"
                  value={dataFimContrato}
                  disabled={disabled}
                  onChange={(e) => setDataFimContrato(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>
      )}

      <div className="rec-envio-proposta-actions">
        <button
          type="button"
          className="rec-btn rec-btn-primary"
          disabled={disabled || saving}
          onClick={handleSave}
        >
          {saving ? 'A guardar…' : 'Guardar'}
        </button>
      </div>

      {savedLabel && (
        <p className="rec-motivo-meta">
          {propostaEnviada === false && motivoNaoEnviada ? (
            <>
              Motivo: {motivoLabelMap.get(motivoNaoEnviada) || motivoNaoEnviada}
              {motivoNaoEnviada === 'outro' && motivoNaoEnviadaOutro
                ? ` — ${motivoNaoEnviadaOutro}`
                : ''}
              {motivoNaoEnviada === 'fidelizado' && (fornecedorAtual || dataFimContrato)
                ? ` — ${fornecedorAtual || 'Fornecedor por definir'}${dataFimContrato ? ` (fim: ${dataFimContrato})` : ''}`
                : ''}
              <br />
            </>
          ) : null}
          Última atualização{meta.updated_by_name ? ` por ${meta.updated_by_name}` : ''}: {savedLabel}
        </p>
      )}
    </div>
  );
};

export default RecuperacaoEnvioPropostaForm;
